import * as React from 'react';
import {
    Box,
    Card,
    // CardActions,
    // Button,
    Link,
    Typography,
    useTheme,
    SimplePaletteColorOptions,
} from '@mui/material';
// import HomeIcon from '@mui/icons-material/Home';
// import CodeIcon from '@mui/icons-material/Code';
import { useTranslate, useDataProvider } from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';

import publishArticleImage from './welcome_illustration.svg';

declare const __BUILD_TIME__: string;
declare const __BUILD_MODE__: string;
declare const __BUILD_COMMIT__: string;
declare const __BUILD_SOURCE__: string;

const BB_BASE = 'https://bitbucket.org/oppimisen-palvelut';

const CommitLink = ({ hash, repo }: { hash: string; repo: string }) => {
    if (!hash) return null;
    const short = hash.substring(0, 7);
    return (
        <Link
            href={`${BB_BASE}/${repo}/commits/${hash}`}
            target="_blank"
            rel="noopener"
            sx={{ color: 'inherit', textDecoration: 'underline' }}
        >
            {short}
        </Link>
    );
};

const BuildInfo = () => {
    const buildTime =
        typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : '';
    const buildMode =
        typeof __BUILD_MODE__ !== 'undefined' ? __BUILD_MODE__ : '';
    const buildCommit =
        typeof __BUILD_COMMIT__ !== 'undefined' ? __BUILD_COMMIT__ : '';
    const buildSource =
        typeof __BUILD_SOURCE__ !== 'undefined' ? __BUILD_SOURCE__ : '';

    if (!buildTime) return null;

    const formatted = new Date(buildTime).toLocaleString('fi-FI', {
        timeZone: 'Europe/Helsinki',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <Box sx={{ mt: 1, opacity: 0.8 }}>
            <Typography variant="caption">
                Frontend: {formatted} | {buildMode} |{' '}
                <CommitLink hash={buildCommit} repo="infra2023" /> |{' '}
                {buildSource}
            </Typography>
        </Box>
    );
};

const BackendInfo = () => {
    const [info, setInfo] = React.useState<any>(null);
    const dataProvider = useDataProvider();

    React.useEffect(() => {
        dataProvider
            .getAny('ping/detail', '')
            .then(({ data }) => setInfo(data))
            .catch(() => {});
    }, [dataProvider]);

    if (!info) return null;

    const buildTime = info.build_timestamp
        ? new Date(info.build_timestamp.replace('_', 'T') + 'Z').toLocaleString(
              'fi-FI',
              {
                  timeZone: 'Europe/Helsinki',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
              }
          )
        : '—';

    const uptime = info.uptime
        ? `${Math.floor(info.uptime / 3600)}h ${Math.floor((info.uptime % 3600) / 60)}m`
        : '—';

    // version format: "20.11.0:commitHash"
    const commitHash = info.version?.split(':')?.[1] || '';

    return (
        <Box sx={{ opacity: 0.8 }}>
            <Typography variant="caption">
                Backend: {buildTime} |{' '}
                <CommitLink hash={commitHash} repo="oppimateriaalit-api" /> |{' '}
                uptime {uptime} | {info.message}
            </Typography>
        </Box>
    );
};

const Welcome = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const { canAccess: canSeeBuildInfo } = useCanAccess({
        action: 'debug',
        resource: 'application',
    });

    const primaryColor = theme?.palette?.primary as SimplePaletteColorOptions;

    return (
        <Card
            sx={{
                background: theme =>
                    `linear-gradient(to bottom, #ffffff33, #ffffff00), linear-gradient(to bottom, ${primaryColor.light}00, ${primaryColor.dark}ff), ${primaryColor.main}`,
                color: '#fff',
                padding: '20px',
            }}
        >
            <Box display="flex">
                <Box flex="1">
                    <Typography variant="h5" component="h2" gutterBottom>
                        {translate('pos.dashboard.welcome.title')}
                    </Typography>
                    <Box maxWidth="40em">
                        <Typography variant="body1" component="p" gutterBottom>
                            {translate('pos.dashboard.welcome.subtitle')}
                        </Typography>
                    </Box>
                    {canSeeBuildInfo && <BuildInfo />}
                    {canSeeBuildInfo && <BackendInfo />}
                </Box>
                <Box
                    display={{ xs: 'none', sm: 'none', md: 'block' }}
                    sx={{
                        background: `url(${publishArticleImage}) top right / cover`,
                        marginLeft: 'auto',
                        alignSelf: 'center',
                    }}
                    width="16em"
                    height="9em"
                    overflow="hidden"
                />
            </Box>
        </Card>
    );
};

export default Welcome;
